import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaClone } from '@react-icons/all-files/fa/FaClone';
import { FaRegCalendarCheck } from '@react-icons/all-files/fa/FaRegCalendarCheck';
import { FaCalendarAlt } from '@react-icons/all-files/fa/FaCalendarAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageForm, { FormField } from '../../../../commons/PageForm';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../../manager/profase-periodo-actividad-instancia.reducer';

const BACK = '/configuracion/calendario/periodos/periodo-instancia';
const PERIODOS = '/configuracion/calendario/periodos';

interface PeriodoInstanciaForm {
  id: string;
  periodoActividad: string;
  periodo: string;
  estado: string;
}

const empty: PeriodoInstanciaForm = {
  id: '',
  periodoActividad: '',
  periodo: '',
  estado: 'NUEVO',
};

const ESTADO_LABEL: Record<string, string> = {
  NUEVO: 'Nuevo',
  GENERADO: 'Generado',
  ARCHIVADO: 'Archivado',
};

function periodoActividadLabel(raw?: any): string | undefined {
  if (!raw) return undefined;
  const tipo = raw.tipoPeriodo ?? '';
  const num = raw.numeroPeriodo != null ? ` #${raw.numeroPeriodo}` : '';
  return raw.nombre ? `${raw.nombre} (${tipo}${num})` : `#${raw.id}`;
}

function periodoLabel(raw?: any): string | undefined {
  if (!raw) return undefined;
  return raw.descripcion?.trim() || `#${raw.id}`;
}

export default function PeriodoInstanciaFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity = useAppSelector(s => s.profasePeriodoActividadInstancia.entity) as any;
  const updating = useAppSelector(s => s.profasePeriodoActividadInstancia.updating);
  const updateSuccess = useAppSelector(s => s.profasePeriodoActividadInstancia.updateSuccess);
  const errorMessage = useAppSelector(s => s.profasePeriodoActividadInstancia.errorMessage);

  const [form, setForm] = useState<PeriodoInstanciaForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof PeriodoInstanciaForm, string>>>({});

  const { alert, showToast } = usePageAlert();

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id!));
    } else {
      dispatch(reset());
      setForm(empty);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNew && entity?.id) {
      setForm({
        id: String(entity.id),
        periodoActividad: entity.periodoActividad?.id != null ? String(entity.periodoActividad.id) : '',
        periodo: entity.periodo?.id != null ? String(entity.periodo.id) : '',
        estado: entity.estado ?? '',
      });
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, {
        state: {
          successMsg: isNew
            ? 'Instancia de periodo creada correctamente.'
            : 'Instancia de periodo actualizada correctamente.',
        },
      });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (errorMessage) showToast('error', errorMessage);
  }, [errorMessage, showToast]);

  const fields: FormField<PeriodoInstanciaForm>[] = useMemo(() => [
    {
      name: 'id',
      label: 'ID',
      readOnly: true,
      editOnly: true,
    },
    {
      name: 'periodoActividad',
      label: 'Definición de periodo',
      type: 'autocomplete-on-demand',
      required: true,
      placeholder: 'Buscar definición por nombre…',
      url: 'api/profase-periodo-actividads',
      queryParam: 'nombre',
      valueKey: 'id',
      labelKey: 'nombre',
      minChars: 0,
      extraParams: { page: '0', size: '50', sort: 'nombre,asc' },
      labelResolver: (_val, raw) => periodoActividadLabel(raw ?? entity?.periodoActividad),
      renderOption: (raw, _opt, isSelected) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ fontWeight: isSelected ? 600 : 400 }}>{raw.nombre ?? `#${raw.id}`}</span>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>
            {raw.tipoPeriodo ?? '—'}
            {raw.numeroPeriodo != null ? ` · nº ${raw.numeroPeriodo}` : ' · todos los números'}
          </span>
        </div>
      ),
    },
    {
      name: 'periodo',
      label: 'Periodo fiscal',
      type: 'autocomplete-on-demand',
      required: true,
      placeholder: 'Buscar por año (ej. 2026)…',
      url: 'api/profase-periodos',
      queryParam: 'anio',
      valueKey: 'id',
      labelKey: 'descripcion',
      minChars: 4,
      extraParams: { page: '0', size: '50', sort: 'desde,desc' },
      labelResolver: (_val, raw) => periodoLabel(raw ?? entity?.periodo),
      renderOption: (raw, _opt, isSelected) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ fontWeight: isSelected ? 600 : 400 }}>{raw.descripcion ?? `#${raw.id}`}</span>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>
            {raw.desde?.slice(0, 10) ?? '—'} → {raw.hasta?.slice(0, 10) ?? '—'}
            {raw.estado ? ` · ${ESTADO_LABEL[raw.estado] ?? raw.estado}` : ''}
          </span>
        </div>
      ),
    },
    {
      name: 'estado',
      label: 'Estado',
      readOnly: true,
      editOnly: true,
      hint: 'El estado lo actualiza el backend (schedulers, archivado de periodo, workflows).',
    },
  ], [entity]);

  const handleFieldChange = (name: keyof PeriodoInstanciaForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Partial<Record<keyof PeriodoInstanciaForm, string>> = {};
    if (!form.periodoActividad) errs.periodoActividad = 'La definición de periodo es requerida';
    if (!form.periodo) errs.periodo = 'El periodo fiscal es requerido';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = {
      ...(isNew ? {} : { id: Number(form.id), estado: entity.estado }),
      ...(isNew ? { estado: 'NUEVO' } : {}),
      periodoActividad: { id: Number(form.periodoActividad) },
      periodo: { id: Number(form.periodo) },
    };

    if (isNew) {
      dispatch(createEntity(payload));
    } else {
      dispatch(updateEntity(payload));
    }
  };

  const displayForm = {
    ...form,
    estado: form.estado ? (ESTADO_LABEL[form.estado] ?? form.estado) : '—',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Calendario', to: '/configuracion/calendario', icon: <FaCalendarAlt /> },
        { label: 'Periodos', to: PERIODOS, icon: <FaRegCalendarCheck /> },
        { label: 'Instancia de Periodo', to: BACK, icon: <FaClone /> },
        { label: isNew ? 'Nueva instancia' : `Editar instancia #${id}` },
      ]} />

      <PageHeader
        icon={<FaClone />}
        iconColor="#8b5cf6"
        title={isNew ? 'Nueva Instancia de Periodo' : 'Editar Instancia de Periodo'}
        subtitle={isNew
          ? 'Vincula una definición de periodo con un periodo fiscal concreto'
          : `Modificando instancia #${id}`}
      />

      {alert && <PageAlert alert={alert} />}

      <PageForm
        fields={fields}
        values={displayForm}
        errors={errors}
        isNew={isNew}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(BACK)}
        accentColor="#8b5cf6"
      />
    </div>
  );
}
